import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Chart from "react-apexcharts";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import Label from "src/components/Label";
import { getTrackerDetailApi } from "src/DAL/tracker/tracker";
import {
  getInitials,
  getLeaveReasonLabel,
  getLeftByDisplay,
  getSubmissionPersonKey,
  getSubmissionReviewLabel,
  getSubmissionSourceLabel,
  isBmsMemberSource,
  isCheckInReviewRow,
  isScheduledReviewDetail,
  loadSubmissionDetailFromApi,
  loadSubmissionsFromApi,
  markSubmissionSeenFromApi,
  sendSubmissionFeedbackApi,
  SUBMISSION_REVIEW,
  updateSubmissionFromApi,
} from "./submissionStorage";
import { normalizeTracker } from "./trackerStorage";
import {
  hasCoachReviewQuestions,
  hasMissingRequiredReviewAnswers,
} from "./CoachReviewPanel";
import ClientFeedbackDrawer from "./ClientFeedbackDrawer";
import PrivateCoachNotesCard from "./PrivateCoachNotesCard";
import {
  buildSubmissionPerformance,
  formatDelta,
  formatMetricValue,
  formatPointLabel,
  parseMeasurableNumber,
} from "./submissionPerformance";
import { backTarget, withBack } from "./trackerNav";

const FEEDBACK_CHANNELS = {
  chat: { label: "Chat", failed: "Chat message not sent" },
  email: { label: "Email", failed: "Email not sent" },
};

const looksLikeHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ""));

const feedbackEntryChannels = (entry = {}) => {
  const results = entry.results || {};
  const keys = new Set(Array.isArray(entry.channels) ? entry.channels : []);
  Object.entries(results).forEach(([key, outcome]) => {
    if (outcome && (outcome.sent || outcome.error)) keys.add(key);
  });
  return Array.from(keys)
    .filter((key) => FEEDBACK_CHANNELS[key])
    .map((key) => ({
      key,
      failed: results[key]?.sent === false,
      error: results[key]?.error || "",
    }));
};

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#DFE3E8";
/** Portal text colours (theme GREY) — not slate-black. */
const TEXT = "#212B36";
const MUTED = "#637381";

const Card = ({ title, description, action, children, sx }) => (
  <Box
    sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: "12px",
      bgcolor: "#fff",
      mb: 2.5,
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      ...sx,
    }}
  >
    {(title || action) && (
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {title ? (
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {title}
            </Typography>
          ) : null}
          {description ? (
            <Typography
              variant="body2"
              sx={{ mt: 0.35, color: "text.secondary", fontSize: 13 }}
            >
              {description}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Box>
    )}
    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>{children}</Box>
  </Box>
);

/** Compact right-rail card (mock-style sidebar). */
const SideCard = ({ title, action, children }) => (
  <Box
    sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: "12px",
      bgcolor: "#fff",
      mb: 1.75,
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    }}
  >
    <Box
      sx={{
        px: 1.75,
        py: 1.35,
        borderBottom: `1px solid ${BORDER}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.45,
          color: MUTED,
          textTransform: "uppercase",
        }}
      >
        {title}
      </Typography>
      {action}
    </Box>
    <Box sx={{ px: 1.75, py: 1.5 }}>{children}</Box>
  </Box>
);

const Fact = ({ label, value }) => (
  <Box
    sx={{
      px: 1.25,
      py: 1,
      borderRadius: "8px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#f8fafc",
      height: "100%",
    }}
  >
    {/* The question, as asked */}
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: MUTED, lineHeight: 1.35 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.35,
        fontSize: 13.5,
        fontWeight: 500,
        color: "text.primary",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const trendMeta = (improved) => {
  if (improved === "up") {
    return {
      color: "#047857",
      bg: "rgba(16,185,129,0.12)",
      label: "Improving",
      Icon: TrendingUpIcon,
    };
  }
  if (improved === "down") {
    return {
      color: "#b91c1c",
      bg: "rgba(239,68,68,0.12)",
      label: "Declining",
      Icon: TrendingDownIcon,
    };
  }
  return {
    color: MUTED,
    bg: "#f1f5f9",
    label: "Stable",
    Icon: TrendingFlatIcon,
  };
};

const overallPerformanceMeta = (metrics = []) => {
  // Verdict from metrics that actually have a trend (2+ readings)
  const trendable = metrics.filter((m) => m.points?.length >= 2);
  const pool = trendable.length ? trendable : metrics;

  if (!pool.length) {
    return {
      label: "No measurable data yet",
      detail: "Add measurable questions to see progress over check-ins.",
      color: MUTED,
      bg: "#f8fafc",
      Icon: TrendingFlatIcon,
    };
  }
  const up = pool.filter((m) => m.improved === "up").length;
  const down = pool.filter((m) => m.improved === "down").length;
  const flat = pool.length - up - down;

  if (up > 0 && down === 0) {
    return {
      label: "On track",
      detail: `${up} metric${up === 1 ? "" : "s"} improving since baseline`,
      color: "#047857",
      bg: "rgba(16,185,129,0.12)",
      Icon: TrendingUpIcon,
      up,
      down,
      flat,
    };
  }
  if (down > 0 && up === 0) {
    return {
      label: "Needs attention",
      detail: `${down} metric${down === 1 ? "" : "s"} moving the wrong way`,
      color: "#b91c1c",
      bg: "rgba(239,68,68,0.12)",
      Icon: TrendingDownIcon,
      up,
      down,
      flat,
    };
  }
  if (up > 0 && down > 0) {
    return {
      label: "Mixed progress",
      detail: `${up} improving · ${down} declining`,
      color: "#b45309",
      bg: "rgba(245,158,11,0.14)",
      Icon: TrendingFlatIcon,
      up,
      down,
      flat,
    };
  }
  return {
    label: "Holding steady",
    detail: "No meaningful change vs baseline yet",
    color: MUTED,
    bg: "#f1f5f9",
    Icon: TrendingFlatIcon,
    up,
    down,
    flat,
  };
};

const MetricChartCard = ({ metric }) => {
  const meta = trendMeta(metric.improved);
  const Icon = meta.Icon;
  const categories = metric.points.map(formatPointLabel);
  const data = metric.points.map((p) => p.value);
  const currentPoint = metric.points.find((p) => p.isCurrent);
  const midLabel = currentPoint ? "THIS CHECK-IN" : "LATEST";
  const midValue = currentPoint
    ? formatMetricValue(currentPoint.value, metric.unit)
    : formatMetricValue(metric.current, metric.unit);

  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: "12px",
        border: `1px solid ${BORDER}`,
        bgcolor: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.25,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: TEXT }}>
            {metric.label}
          </Typography>
          {metric.questions?.length &&
          !(
            metric.questions.length === 1 &&
            metric.questions[0] === metric.label
          ) ? (
            <Typography sx={{ mt: 0.25, fontSize: 12, color: TEXT }}>
              <Box component="span" sx={{ color: MUTED }}>
                {metric.questions.length === 1 ? "Question: " : "Questions: "}
              </Box>
              {metric.questions.join(" → ")}
            </Typography>
          ) : null}
          <Typography sx={{ mt: 0.25, fontSize: 12, color: MUTED }}>
            {metric.points.length} reading
            {metric.points.length === 1 ? "" : "s"}
            {metric.lowerBetter ? " · lower is better" : " · higher is better"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.4,
            borderRadius: "999px",
            bgcolor: meta.bg,
            color: meta.color,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
            {meta.label}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 1,
          mb: 1.25,
          p: 1.1,
          borderRadius: "8px",
          bgcolor: "#f8fafc",
          border: `1px solid ${BORDER}`,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>
            BASELINE
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {formatMetricValue(metric.baseline, metric.unit)}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>
            {midLabel}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: PRIMARY }}>
            {midValue}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>
            CHANGE
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: meta.color }}>
            {formatDelta(metric.delta, metric.unit, metric.improved)}
            {metric.improved !== "flat" && metric.pctChange != null
              ? ` (${metric.pctChange > 0 ? "+" : ""}${metric.pctChange.toFixed(1)}%)`
              : ""}
          </Typography>
        </Box>
      </Box>

      {metric.points.length >= 2 ? (
        <Box sx={{ mt: "auto" }}>
          <Chart
            options={{
              chart: {
                type: "area",
                toolbar: { show: false },
                zoom: { enabled: false },
                fontFamily: "inherit",
                sparkline: { enabled: false },
              },
              colors: [meta.color === MUTED ? PRIMARY : meta.color],
              dataLabels: { enabled: false },
              stroke: { curve: "smooth", width: 2.5 },
              markers: {
                size: 4,
                hover: { size: 6 },
              },
              fill: {
                type: "gradient",
                gradient: {
                  shadeIntensity: 0.35,
                  opacityFrom: 0.35,
                  opacityTo: 0.05,
                  stops: [0, 90, 100],
                },
              },
              grid: { borderColor: "#f1f5f9", strokeDashArray: 3 },
              xaxis: {
                categories,
                labels: { style: { colors: MUTED, fontSize: "10px" } },
              },
              yaxis: {
                labels: {
                  style: { colors: MUTED, fontSize: "10px" },
                  formatter: (v) =>
                    Number.isFinite(v) ? String(Math.round(v * 10) / 10) : "",
                },
                forceNiceScale: true,
              },
              tooltip: {
                theme: "light",
                y: {
                  formatter: (v) => formatMetricValue(v, metric.unit),
                },
              },
            }}
            series={[{ name: metric.label, data }]}
            type="area"
            height={170}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default function TrackerSubmissionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [submission, setSubmission] = useState(null);
  const [journeyRows, setJourneyRows] = useState([]);
  const [tracker, setTracker] = useState(null);
  const [trackerStatuses, setTrackerStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingMeta, setSavingMeta] = useState(false);
  const savingMetaRef = useRef(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setSubmission(null);
      setJourneyRows([]);
      setTracker(null);
      setTrackerStatuses([]);
      try {
        const result = await loadSubmissionDetailFromApi(id);
        if (cancelled) return;

        if (!result.ok || !result.submission) {
          enqueueSnackbar(result.message || "Submission not found", {
            variant: "error",
          });
          navigate("/tracker-submissions", { replace: true });
          return;
        }

        setSubmission(result.submission);

        if (result.submission.isNew) {
          markSubmissionSeenFromApi(result.submission.id).then((seen) => {
            if (!cancelled && seen.ok && seen.submission) {
              setSubmission(seen.submission);
            }
          });
        }

        if (result.submission.trackerId) {
          const [list, trackerRes] = await Promise.all([
            loadSubmissionsFromApi(result.submission.trackerId, {
              limit: 500,
            }),
            getTrackerDetailApi(result.submission.trackerId).catch(() => null),
          ]);
          if (!cancelled) {
            setJourneyRows(list.rows || []);
            if (trackerRes?.code === 200 && trackerRes.tracker) {
              const shaped = normalizeTracker(trackerRes.tracker);
              setTracker(shaped);
              setTrackerStatuses(
                Array.isArray(shaped?.statuses) ? shaped.statuses : []
              );
            }
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, enqueueSnackbar, navigate]);

  const handleMetaUpdate = async (patch) => {
    if (!submission?.id || savingMetaRef.current) return;
    if (patch?.error) {
      enqueueSnackbar(patch.error, { variant: "warning" });
      return;
    }
    const { memberFeedback, ...metaPatch } = patch;
    savingMetaRef.current = true;
    setSavingMeta(true);
    const result = await updateSubmissionFromApi(submission.id, metaPatch);
    if (!result.ok) {
      savingMetaRef.current = false;
      setSavingMeta(false);
      enqueueSnackbar(result.message || "Could not update", {
        variant: "error",
      });
      return;
    }
    let nextSubmission = result.submission;
    let notifyIncomplete = false;
    if (
      memberFeedback?.message &&
      (memberFeedback.sendChat || memberFeedback.sendEmail)
    ) {
      const sent = await sendSubmissionFeedbackApi(submission.id, {
        message: memberFeedback.message,
        sendChat: Boolean(memberFeedback.sendChat),
        sendEmail: Boolean(memberFeedback.sendEmail),
      });
      if (!sent.ok) {
        savingMetaRef.current = false;
        setSavingMeta(false);
        setSubmission(nextSubmission);
        enqueueSnackbar(
          sent.message || "Review saved, but feedback could not be sent",
          { variant: "warning" }
        );
        return;
      }
      if (sent.submission) nextSubmission = sent.submission;
      if (sent.results?.chat?.sent) {
        enqueueSnackbar("Chat message sent", { variant: "success" });
      } else if (memberFeedback.sendChat && sent.results?.chat) {
        notifyIncomplete = true;
        enqueueSnackbar(
          sent.results.chat.error || "Chat message not sent",
          { variant: "warning" }
        );
      }
      if (sent.results?.email?.sent) {
        enqueueSnackbar("Email sent", { variant: "success" });
      } else if (memberFeedback.sendEmail && sent.results?.email) {
        notifyIncomplete = true;
        enqueueSnackbar(
          sent.results.email.error || "Email not sent",
          { variant: "warning" }
        );
      }
    }
    savingMetaRef.current = false;
    setSavingMeta(false);
    setSubmission(nextSubmission);
    const dropped =
      metaPatch.statusName != null &&
      /drop/i.test(String(metaPatch.statusName));
    if (!notifyIncomplete) {
      enqueueSnackbar(
        dropped
          ? "Member dropped out of the journey"
          : metaPatch.reviewStatus === "reviewed"
            ? "Review completed"
            : "Submission updated",
        { variant: "success" }
      );
    } else if (metaPatch.reviewStatus === "reviewed") {
      enqueueSnackbar("Review saved (some messages did not send)", {
        variant: "warning",
      });
    }
    if (dropped && nextSubmission) {
      const list = await loadSubmissionsFromApi(nextSubmission.trackerId, {
        limit: 500,
      });
      if (list?.ok) setJourneyRows(list.rows || []);
    }
  };

  const performance = useMemo(
    () =>
      submission
        ? buildSubmissionPerformance(submission, journeyRows, tracker)
        : null,
    [submission, journeyRows, tracker]
  );

  const journeyHistory = useMemo(() => {
    if (!submission?.trackerId) return [];
    const personKey = getSubmissionPersonKey(submission);
    return (journeyRows || [])
      .filter((row) => getSubmissionPersonKey(row) === personKey)
      // The open submission changes on this page (Mark reviewed, feedback,
      // status…) — show its live state, not the copy loaded with the list.
      .map((row) =>
        row.id === submission.id
          ? {
              ...row,
              reviewStatus: submission.reviewStatus,
              reviewedAt: submission.reviewedAt,
              reviewDetail: submission.reviewDetail,
              statusName: submission.statusName,
              statusColor: submission.statusColor,
              coachId: submission.coachId,
              coachName: submission.coachName,
            }
          : row
      )
      .sort(
        (a, b) =>
          new Date(a.submittedAt || 0) - new Date(b.submittedAt || 0)
      );
  }, [submission, journeyRows]);

  // Member left, or coach/admin removed / Dropped Out. Reactivation uses
  // statusBeforeLeave on the stop row.
  const leaveInfo = useMemo(() => {
    if (!submission) return null;
    const isLeftName = (name) => String(name || "").toLowerCase() === "left";
    const isDropName = (name) => /drop/i.test(String(name || ""));
    const isStopRow = (row) =>
      Boolean(row?.leftAt) ||
      isLeftName(row?.statusName) ||
      isDropName(row?.statusName);
    if (!isStopRow(submission)) return null;
    const leftRow =
      [...journeyHistory].reverse().find((r) => isStopRow(r)) || submission;
    const defaultStatus =
      (trackerStatuses || []).find((s) => s?.assignedOnSubmit) ||
      (trackerStatuses || [])[0] ||
      null;
    const leftAt = leftRow.leftAt || submission.leftAt;
    const leftBy = leftRow.leftBy || submission.leftBy || "";
    const leftByName = leftRow.leftByName || submission.leftByName || "";
    const leftByDisplay = getLeftByDisplay({ leftBy, leftByName });
    const staffRemoved = leftBy === "coach" || leftBy === "admin";
    const freeReason = String(
      leftRow.leftReason || submission.leftReason || ""
    ).trim();
    const exitTitle = staffRemoved
      ? "Dropped out"
      : isDropName(leftRow.statusName || submission.statusName)
        ? "Dropped out"
        : "Left the journey";
    return {
      rowId: leftRow.id,
      statusName:
        leftRow.statusBeforeLeave ||
        defaultStatus?.name ||
        defaultStatus?.label ||
        "Submitted",
      title: leftByDisplay ? `${exitTitle} · by ${leftByDisplay}` : exitTitle,
      reasonLabel:
        getLeaveReasonLabel(freeReason) || (staffRemoved ? freeReason : ""),
      note: leftRow.leftNote || submission.leftNote || "",
      dateLabel: leftAt ? moment(leftAt).format("DD MMM YYYY") : "",
      leftBy,
      leftByName,
      leftByDisplay,
    };
  }, [submission, journeyHistory, trackerStatuses]);

  const handleReactivate = async () => {
    if (!leaveInfo || !submission?.id) return;
    const { rowId, statusName } = leaveInfo;
    if (rowId === submission.id) {
      await handleMetaUpdate({ statusName });
      return;
    }
    if (savingMeta) return;
    setSavingMeta(true);
    const result = await updateSubmissionFromApi(rowId, { statusName });
    const refreshed = result.ok
      ? await loadSubmissionDetailFromApi(submission.id)
      : null;
    setSavingMeta(false);
    if (!result.ok) {
      enqueueSnackbar(result.message || "Could not reactivate", {
        variant: "error",
      });
      return;
    }
    setJourneyRows((rows) =>
      rows.map((r) => (r.id === result.submission.id ? result.submission : r))
    );
    if (refreshed?.ok && refreshed.submission) {
      setSubmission(refreshed.submission);
    }
    enqueueSnackbar("Member reactivated", { variant: "success" });
  };

  // Merge only the feedback fields so an in-progress coach review isn't reset.
  const handleFeedbackSent = async (updated) => {
    if (!submission?.id) return;
    let fresh = updated;
    if (!fresh) {
      const refreshed = await loadSubmissionDetailFromApi(submission.id);
      fresh = refreshed.ok ? refreshed.submission : null;
    }
    if (!fresh) return;
    setSubmission((prev) =>
      prev && prev.id === fresh.id
        ? {
            ...prev,
            coachFeedback: fresh.coachFeedback,
            coachNotes: fresh.coachNotes,
            // Feedback on a due review marks it reviewed (backend).
            reviewStatus: fresh.reviewStatus,
            reviewedAt: fresh.reviewedAt,
            reviewDetail: fresh.reviewDetail,
          }
        : prev
    );
  };

  const answerGroups = useMemo(() => {
    if (!submission) return { measurable: [], other: [] };
    const metricLabels = new Set(
      (performance?.metrics || []).map((m) =>
        String(m.label || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, " ")
          .trim()
      )
    );
    // Already shown in the user header card — skip in Answers.
    const isBasicContactField = (key) => {
      const norm = String(key || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
      return /^(first name|last name|full name|name|email|email address|phone|phone number|mobile|mobile number|contact number|contact)$/.test(
        norm
      );
    };
    // Question type from the journey's own questions: label → measurable?
    const plainLabel = (label) =>
      String(label || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    const questionIsMeasurable = new Map();
    [
      ...(tracker?.sharedQuestions || []),
      ...(tracker?.forms || []).flatMap((form) => form?.questions || []),
    ].forEach((q) => {
      const label = plainLabel(q?.label);
      if (!label) return;
      questionIsMeasurable.set(
        label,
        q.measurable === true ||
          String(q.mode || "").toLowerCase() === "measurable" ||
          Boolean(String(q.measureType || "").trim())
      );
    });
    const measurable = [];
    const other = [];
    Object.entries(submission.answers || {}).forEach(([key, value]) => {
      if (isBasicContactField(key)) return;
      const known = questionIsMeasurable.get(plainLabel(key));
      const norm = plainLabel(key).replace(/^(starting|current)\s+/, "");
      // Unknown (e.g. older answers): guess from the value and label.
      const isMetric =
        known !== undefined
          ? known
          : parseMeasurableNumber(value) != null &&
            (metricLabels.has(norm) ||
              /weight|body\s*fat|bmi|energy|sessions|score|height|adherence/i.test(
                key
              ));
      if (isMetric) measurable.push([key, value]);
      else other.push([key, value]);
    });
    return { measurable, other };
  }, [submission, performance, tracker]);

  if (loading || !submission) {
    return (
      <div className="container-fluid tracker-module-page">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "55vh",
            width: "100%",
          }}
        >
          <CircularProgress size={40} color="primary" />
        </Box>
      </div>
    );
  }

  const metrics = performance?.metrics || [];
  const sameLabel = (a, b) =>
    String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
  // "17" → "17 kg" using the unit of the graph built from that question.
  const withUnit = (question, value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return "—";
    if (!/^-?\d+(\.\d+)?$/.test(raw)) return raw;
    const metric = metrics.find((m) =>
      (m.questions || []).some((q) => sameLabel(q, question))
    );
    return metric?.unit ? `${raw} ${metric.unit}` : raw;
  };
  const displayMetrics = metrics.filter(
    (m) =>
      m.points.length >= 2 ||
      m.points.some((p) => p.isCurrent) ||
      m.improved !== "flat"
  );
  const overall = overallPerformanceMeta(metrics);
  const OverallIcon = overall.Icon;
  const feedbackHistory = [...(submission.coachFeedback || [])].sort(
    (a, b) => new Date(b?.sentAt || 0) - new Date(a?.sentAt || 0)
  );
  // Back to the list as the user left it — never add a tracker filter.
  // Back: the list, tracker tab or review this submission was opened from.
  const back = backTarget(location, navigate, "/tracker-submissions", "Back to submissions");

  // Form name + submitted time already sit under the page title.
  const answersMeta = submission.submittedByName
    ? `On behalf, by ${submission.submittedByName}`
    : "";

  const scheduleInfo = submission.reviewDetail
    ? submission.reviewDetail
    : submission.reviewStatus === SUBMISSION_REVIEW.REVIEWED
      ? "Every milestone on this entry has been signed off."
      : submission.reviewStatus === SUBMISSION_REVIEW.OVERDUE
        ? "Coach review is overdue for this submission."
        : submission.reviewStatus === SUBMISSION_REVIEW.PENDING
          ? "Coach review is pending for this submission."
          : "No upcoming review scheduled for this entry.";

  // Oldest first (ascending by date).
  const relatedSubs = journeyHistory;

  return (
    <div className="container-fluid tracker-module-page">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <IconButton
          size="small"
          onClick={back.go}
          sx={{ p: 0.75 }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          component="button"
          type="button"
          onClick={back.go}
          sx={{
            border: 0,
            background: "none",
            p: 0,
            cursor: "pointer",
            color: MUTED,
            fontSize: 13,
            "&:hover": { color: PRIMARY },
          }}
        >
          {back.label}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {submission.trackerId ? (
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={() => navigate(`/trackers/${submission.trackerId}`, { state: withBack() })}
          >
            Open tracker
          </button>
        ) : null}
      </Box>

      {/* Journey this submission belongs to — prominent at the top */}
      <Box sx={{ mb: 1.75 }}>
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: MUTED,
          }}
        >
          Journey
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mt: 0.25,
          }}
        >
          <Typography
            component={submission.trackerId ? "button" : "span"}
            type={submission.trackerId ? "button" : undefined}
            onClick={
              submission.trackerId
                ? () => navigate(`/trackers/${submission.trackerId}`, { state: withBack() })
                : undefined
            }
            sx={{
              p: 0,
              border: 0,
              background: "none",
              textAlign: "left",
              fontSize: { xs: 20, sm: 24 },
              fontWeight: 800,
              lineHeight: 1.25,
              color: TEXT,
              cursor: submission.trackerId ? "pointer" : "default",
              "&:hover": submission.trackerId ? { color: PRIMARY } : undefined,
            }}
          >
            {submission.trackerName || "Untitled journey"}
          </Typography>
        </Box>
        {submission.submittedAt ? (
          <Typography sx={{ mt: 0.25, fontSize: 13, color: MUTED }}>
            Submitted {moment(submission.submittedAt).format("D MMM YYYY, h:mm A")}
          </Typography>
        ) : null}
      </Box>

      {/* User summary card */}
      <Box
        sx={{
          mb: 2,
          p: { xs: 2, sm: 2.25 },
          borderRadius: "12px",
          border: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Avatar
          sx={{
            width: 56,
            height: 56,
            fontSize: 18,
            fontWeight: 700,
            bgcolor: PRIMARY_SOFT,
            color: PRIMARY,
            flexShrink: 0,
          }}
        >
          {getInitials(submission.memberName)}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mb: 0.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, lineHeight: 1.3, color: "text.primary" }}
            >
              {submission.memberName}
            </Typography>
            <Label
              variant="ghost"
              color={
                isBmsMemberSource(submission.source) ? "warning" : "info"
              }
            >
              {getSubmissionSourceLabel(submission.source)}
            </Label>
          </Box>
          {submission.email ? (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: 13 }}
            >
              {submission.email}
            </Typography>
          ) : null}
          {submission.phone || submission.contactNumber ? (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: 13 }}
            >
              {submission.phone || submission.contactNumber}
            </Typography>
          ) : null}
        </Box>

        {/* This person on the journey at a glance */}
        <Box
          sx={{
            flex: "2 1 360px",
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, minmax(0, 1fr))" },
            gap: 1,
          }}
        >
          {[
            {
              label: "Status",
              value: (
                <Box
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: submission.statusColor || PRIMARY,
                      flexShrink: 0,
                    }}
                  />
                  {submission.statusName || "—"}
                </Box>
              ),
            },
            {
              label: "Check-ins",
              value: `${journeyHistory.length || 1} on this journey`,
            },
            {
              label: "Joined",
              value: journeyHistory[0]?.submittedAt
                ? moment(journeyHistory[0].submittedAt).format("D MMM YYYY")
                : "—",
            },
            {
              label: "Last check-in",
              value: journeyHistory.length
                ? moment(
                    journeyHistory[journeyHistory.length - 1].submittedAt
                  ).fromNow()
                : "—",
            },
          ].map((tile) => (
            <Box
              key={tile.label}
              sx={{
                p: 1.25,
                borderRadius: "10px",
                border: `1px solid ${BORDER}`,
                bgcolor: "#f8fafc",
                minWidth: 0,
              }}
            >
              <Typography
                sx={{ fontSize: 11.5, fontWeight: 600, color: MUTED, mb: 0.35 }}
              >
                {tile.label}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: TEXT,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {tile.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {leaveInfo ? (
        <Alert
          severity="info"
          sx={{ mb: 2, alignItems: "center", borderRadius: "12px" }}
          action={
            <button
              type="button"
              className="small-contained-button"
              disabled={savingMeta}
              onClick={handleReactivate}
            >
              Reactivate
            </button>
          }
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
            {[leaveInfo.title, leaveInfo.reasonLabel, leaveInfo.dateLabel]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          {leaveInfo.note ? (
            <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>
              “{leaveInfo.note}”
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      <ClientFeedbackDrawer
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        submission={submission}
        metrics={metrics}
        onSent={handleFeedbackSent}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 320px" },
          gap: { xs: 0, lg: 2.5 },
          alignItems: "start",
        }}
      >
        {/* LEFT — Answers */}
        <Box sx={{ minWidth: 0 }}>
          <Card title="What they answered" description={answersMeta}>
            {answerGroups.measurable.length === 0 &&
            answerGroups.other.length === 0 ? (
              <Typography sx={{ fontSize: 13.5, color: MUTED }}>
                No answers stored on this submission.
              </Typography>
            ) : (
              <Box sx={{ display: "grid", gap: 2 }}>
                {answerGroups.measurable.length > 0 ? (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        color: MUTED,
                        textTransform: "uppercase",
                        mb: 1,
                      }}
                    >
                      Measurable questions
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr 1fr",
                          sm: "repeat(3, minmax(0, 1fr))",
                          xl: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 1,
                      }}
                    >
                      {answerGroups.measurable.map(([key, value]) => (
                        <Box
                          key={key}
                          sx={{
                            px: 1.25,
                            py: 1,
                            borderRadius: "8px",
                            border: `1px solid ${BORDER}`,
                            bgcolor: PRIMARY_SOFT,
                            minWidth: 0,
                          }}
                        >
                          {/* The question, as asked */}
                          <Typography
                            title={key}
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: MUTED,
                              lineHeight: 1.35,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {key}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.25,
                              fontSize: 16,
                              fontWeight: 700,
                              color: TEXT,
                              letterSpacing: "-0.01em",
                            }}
                          >
                            {withUnit(key, value)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {answerGroups.other.length > 0 ? (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        color: MUTED,
                        textTransform: "uppercase",
                        mb: 1,
                      }}
                    >
                      General questions
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                        },
                        gap: 1,
                      }}
                    >
                      {answerGroups.other.map(([key, value]) => (
                        <Fact
                          key={key}
                          label={key}
                          value={String(value || "—")}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            )}
          </Card>

          <Box
            sx={{
              mb: 2.5,
              p: { xs: 2, sm: 2.25 },
              borderRadius: "12px",
              border: `1px solid ${BORDER}`,
              bgcolor: "#fff",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(220px, 1.1fr) repeat(2, minmax(0, 1fr))",
              },
              gap: 1.5,
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pr: { md: 1.5 },
                borderRight: { md: `1px solid ${BORDER}` },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  bgcolor: overall.bg,
                  color: overall.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <OverallIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.45,
                    color: MUTED,
                    textTransform: "uppercase",
                  }}
                >
                  Performance
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 20,
                    color: overall.color,
                    lineHeight: 1.2,
                  }}
                >
                  {overall.label}
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: 12.5, color: MUTED }}>
                  {overall.detail}
                </Typography>
              </Box>
            </Box>

            {[
              {
                label: "Improving",
                value: String(overall.up ?? 0),
                hint: "metrics vs baseline",
                color: "#047857",
              },
              {
                label: "Needs work",
                value: String(overall.down ?? 0),
                hint: "metrics vs baseline",
                color: overall.down ? "#b91c1c" : MUTED,
              },
            ].map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  px: { md: 1 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    color: MUTED,
                    textTransform: "uppercase",
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    fontSize: 26,
                    fontWeight: 600,
                    color: stat.color || "#0f172a",
                    lineHeight: 1.1,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography sx={{ mt: 0.2, fontSize: 12, color: MUTED }}>
                  {stat.hint}
                </Typography>
              </Box>
            ))}
          </Box>

          {displayMetrics.length > 0 ? (
            <Card
              title="Measurable progress"
              description="Baseline → this check-in, across every reading for this person on the journey."
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: displayMetrics.length === 1 ? "1fr" : "1fr 1fr",
                  },
                  gap: 1.5,
                }}
              >
                {displayMetrics.map((metric) => (
                  <MetricChartCard key={metric.id} metric={metric} />
                ))}
              </Box>
            </Card>
          ) : null}
        </Box>

        {/* RIGHT — review + feedback first (most important), then coach,
            private notes, related */}
        <Box sx={{ minWidth: 0 }}>
          <SideCard title="Check-in review">
            <Typography sx={{ fontSize: 13.5, color: "text.primary", mb: 1 }}>
              {isCheckInReviewRow(submission)
                ? submission.reviewDetail ||
                  "Chat/email feedback for this check-in."
                : isScheduledReviewDetail(submission.reviewDetail)
                  ? "Scheduled journey review is on the Reviews menu."
                  : scheduleInfo}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Label
                variant="ghost"
                color={
                  submission.reviewStatus === SUBMISSION_REVIEW.OVERDUE
                    ? "error"
                    : submission.reviewStatus === SUBMISSION_REVIEW.PENDING
                      ? "warning"
                      : submission.reviewStatus === SUBMISSION_REVIEW.REVIEWED
                        ? "success"
                        : "default"
                }
              >
                {getSubmissionReviewLabel(submission.reviewStatus)}
              </Label>
              {isCheckInReviewRow(submission) &&
              (submission.reviewStatus === SUBMISSION_REVIEW.PENDING ||
                submission.reviewStatus === SUBMISSION_REVIEW.OVERDUE) ? (
                <button
                  type="button"
                  className="small-contained-button"
                  disabled={savingMeta}
                  // Required review questions still open → answer them on the review page.
                  onClick={() =>
                    hasMissingRequiredReviewAnswers(tracker, submission)
                      ? navigate(`/tracker-reviews/${submission.id}`, { state: withBack() })
                      : handleMetaUpdate({ reviewStatus: "reviewed" })
                  }
                  style={{ fontSize: 12, padding: "4px 10px" }}
                >
                  Mark reviewed
                </button>
              ) : null}
              {/* Required review questions block Mark reviewed — answer them on the review page. */}
              {isCheckInReviewRow(submission) &&
              (submission.reviewStatus === SUBMISSION_REVIEW.PENDING ||
                submission.reviewStatus === SUBMISSION_REVIEW.OVERDUE) &&
              hasCoachReviewQuestions(tracker) ? (
                <button
                  type="button"
                  className="small-contained-outlined-button"
                  onClick={() =>
                    navigate(`/tracker-reviews/${submission.id}`, { state: withBack() })
                  }
                  style={{ fontSize: 12, padding: "4px 10px" }}
                >
                  Answer review questions
                </button>
              ) : null}
              {isScheduledReviewDetail(submission.reviewDetail) ? (
                <button
                  type="button"
                  className="small-contained-outlined-button"
                  onClick={() =>
                    navigate(`/tracker-reviews/${submission.id}`, { state: withBack() })
                  }
                  style={{ fontSize: 12, padding: "4px 10px" }}
                >
                  Open scheduled review
                </button>
              ) : null}
            </Box>
          </SideCard>

          <SideCard
            title={`Feedback${
              feedbackHistory.length ? ` (${feedbackHistory.length})` : ""
            }`}
            action={
              <button
                type="button"
                className="small-contained-button"
                onClick={() => setFeedbackOpen(true)}
                style={{ fontSize: 12, padding: "4px 10px" }}
              >
                Add
              </button>
            }
          >
            {feedbackHistory.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                No chat/email notes yet.
              </Typography>
            ) : (
              <Box sx={{ display: "grid", gap: 1 }}>
                {feedbackHistory.slice(0, 5).map((entry, index) => (
                  <Box
                    key={`${entry?.sentAt || ""}-${index}`}
                    sx={{
                      p: 1.15,
                      borderRadius: "8px",
                      border: `1px solid ${BORDER}`,
                      bgcolor: "#f8fafc",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        flexWrap: "wrap",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 11.5,
                          fontWeight: 700,
                          color: TEXT,
                        }}
                      >
                        {entry?.sentAt
                          ? moment(entry.sentAt).format("DD MMM h:mm A")
                          : "—"}
                      </Typography>
                      {feedbackEntryChannels(entry).map((channel) => (
                        <Chip
                          key={channel.key}
                          size="small"
                          label={FEEDBACK_CHANNELS[channel.key].label}
                          sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                        />
                      ))}
                    </Box>
                    {looksLikeHtml(entry?.message) ? (
                      <Box
                        sx={{
                          fontSize: 12.5,
                          color: "text.primary",
                          wordBreak: "break-word",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          "& p": { m: 0 },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: entry?.message || "",
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          color: "text.primary",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {entry?.message || ""}
                      </Typography>
                    )}
                  </Box>
                ))}
                {feedbackHistory.length > 5 ? (
                  <Typography sx={{ fontSize: 12, color: MUTED }}>
                    +{feedbackHistory.length - 5} more — open Add to view all
                  </Typography>
                ) : null}
              </Box>
            )}
          </SideCard>

          {/* The coach sees their own submissions; the assigned coach card is not needed here. */}
          <Box sx={{ mb: 2 }}>
            <PrivateCoachNotesCard submission={submission} />
          </Box>

          <SideCard
            title={`Submissions on this tracker${
              relatedSubs.length ? ` (${relatedSubs.length})` : ""
            }`}
          >
            {relatedSubs.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                No other submissions for this person.
              </Typography>
            ) : (
              <>
                <Box sx={{ display: "grid", gap: 0.75 }}>
                  {relatedSubs.map((row) => {
                    const active = row.id === submission.id;
                    return (
                      <Box
                        key={row.id}
                        component="button"
                        type="button"
                        onClick={() => {
                          if (!active)
                            navigate(`/tracker-submissions/${row.id}`, {
                              state: location.state,
                            });
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          textAlign: "left",
                          width: "100%",
                          border: `1px solid ${active ? PRIMARY : BORDER}`,
                          bgcolor: active ? PRIMARY_SOFT : "#fff",
                          borderRadius: "8px",
                          px: 1.15,
                          py: 0.9,
                          cursor: active ? "default" : "pointer",
                          "&:hover": active
                            ? undefined
                            : { borderColor: PRIMARY, bgcolor: "#f8fafc" },
                        }}
                      >
                        {/* Date + review status only (no form name). */}
                        <Typography
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 13,
                            fontWeight: 700,
                            color: TEXT,
                          }}
                        >
                          {row.submittedAt
                            ? moment(row.submittedAt).format("D MMM YYYY")
                            : "—"}
                        </Typography>
                        <Label
                          variant="ghost"
                          color={
                            row.reviewStatus === SUBMISSION_REVIEW.OVERDUE
                              ? "error"
                              : row.reviewStatus === SUBMISSION_REVIEW.PENDING
                                ? "warning"
                                : row.reviewStatus === SUBMISSION_REVIEW.REVIEWED
                                  ? "success"
                                  : "default"
                          }
                        >
                          {row.reviewStatus &&
                          row.reviewStatus !== SUBMISSION_REVIEW.NONE
                            ? getSubmissionReviewLabel(row.reviewStatus)
                            : "No review"}
                        </Label>
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </SideCard>
        </Box>
      </Box>
    </div>
  );
}
